import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/upr-api";
import { ArrowUpRight, X } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import DOMPurify from "dompurify";
import { RICH_TEXT_SANITIZE } from "@/lib/rich-text";

const PAGE_SIZE = 12;

const FEATURED_SIZE_MAP = {
  small: { wrap: "md:col-span-5", side: "md:col-span-7" },
  medium: { wrap: "md:col-span-7", side: "md:col-span-5" },
  large: { wrap: "md:col-span-9", side: "md:col-span-3" },
};

export default function Music() {
  const { settings } = useSettings();
  const heroHtml = (settings?.hero_html || "").trim();
  const heroLine1 = settings?.hero_title_line1 || "Music";
  const heroLine2 = settings?.hero_title_line2 || "from the sun.";
  const heroSubtitle = settings?.hero_subtitle || "Universo Positivo is an independent record label rooted in Lisbon and Amsterdam, releasing handmade music that travels between sunlit rooms and late-night dance floors.";
  const heroFontClass = `upr-hero-font-${settings?.hero_font || "sans"}`;
  const marqueeWords = (settings?.marquee_words || "UNIVERSO POSITIVO, MUSIC FROM THE SUN, EST. SINCE THE SUN, LISBOA × AMSTERDAM, UPR RECORDS")
    .split(",").map((w) => w.trim()).filter(Boolean);
  const featuredSize = FEATURED_SIZE_MAP[settings?.featured_image_size] || FEATURED_SIZE_MAP.medium;
  const [releases, setReleases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = (searchParams.get("tag") || "").toLowerCase();

  useEffect(() => {
    setLoading(true);
    if (activeTag) {
      // Tag filtering happens client-side, so we need the full catalog.
      Promise.all([
        api.get("/releases", { params: { limit: 500, offset: 0 } }),
        api.get("/releases/count"),
      ])
        .then(([rel, count]) => {
          setReleases(rel.data);
          setTotal(count.data.total || rel.data.length);
        })
        .finally(() => setLoading(false));
      return;
    }
    Promise.all([
      api.get("/releases", { params: { limit: PAGE_SIZE, offset: 0 } }),
      api.get("/releases/count"),
    ])
      .then(([rel, count]) => {
        setReleases(rel.data);
        setTotal(count.data.total || rel.data.length);
      })
      .finally(() => setLoading(false));
  }, [activeTag]);

  const loadMore = async () => {
    if (loadingMore || releases.length >= total) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/releases", { params: { limit: PAGE_SIZE, offset: releases.length } });
      setReleases((prev) => [...prev, ...data]);
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = activeTag
    ? releases.filter((r) => (r.tags || []).some((t) => String(t).toLowerCase() === activeTag))
    : releases;

  const featured = !activeTag ? (releases.find((r) => r.featured) || releases[0]) : null;
  const rest = activeTag ? filtered : filtered.filter((r) => !featured || r.id !== featured.id);

  const clearTag = () => {
    const sp = new URLSearchParams(searchParams);
    sp.delete("tag");
    setSearchParams(sp, { replace: true });
  };

  return (
    <div data-testid="music-page">
      {/* HERO */}
      <section className="relative pt-12 md:pt-20 pb-16 md:pb-24 overflow-hidden" data-testid="hero">
        <div className="px-6 md:px-12 lg:px-16">
          <div className="upr-eyebrow mb-6"><span className="upr-divider"/>{activeTag ? "Filtered by tag" : "Catalogue · Releases"}</div>
          {activeTag ? (
            <>
              <h1 className="font-heading font-black tracking-tighter leading-[0.85] text-[14vw] md:text-[9.5vw] lg:text-[8.4rem] uppercase" style={{ letterSpacing: "-0.04em" }} data-testid="tag-page-title">
                <span style={{ color: "var(--upr-primary)" }}>#{activeTag}</span>
              </h1>
              <div className="mt-4 flex items-center gap-3">
                <p className="text-base md:text-lg" style={{ color: "var(--upr-muted)" }}>
                  {filtered.length} release{filtered.length === 1 ? "" : "s"} tagged with <strong className="uppercase">{activeTag}</strong>.
                </p>
                <button
                  type="button"
                  onClick={clearTag}
                  data-testid="clear-tag"
                  className="upr-eyebrow inline-flex items-center gap-1 hover:text-[color:var(--upr-primary)]"
                >
                  <X size={12} /> Clear filter
                </button>
              </div>
            </>
          ) : heroHtml ? (
            <div
              className={`upr-hero-rich ${heroFontClass} font-heading font-black tracking-tighter leading-[0.9] text-[14vw] md:text-[9.5vw] lg:text-[8.4rem]`}
              style={{ letterSpacing: "-0.04em" }}
              data-testid="hero-rich"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroHtml, RICH_TEXT_SANITIZE) }}
            />
          ) : (
            <>
              <h1 className={`${heroFontClass} font-heading font-black tracking-tighter leading-[0.85] text-[14vw] md:text-[9.5vw] lg:text-[8.4rem]`} style={{ letterSpacing: "-0.04em" }} data-testid="hero-title">
                {heroLine1}<br/>
                <span style={{ color: "var(--upr-primary)" }}>{heroLine2}</span>
              </h1>
              <p className="mt-6 max-w-xl text-base md:text-lg" style={{ color: "var(--upr-muted)" }}>
                {heroSubtitle}
              </p>
            </>
          )}
        </div>

        {!activeTag && marqueeWords.length > 0 && (
          <div className="mt-14 overflow-hidden border-y" style={{ borderColor: "var(--upr-border)" }}>
            <div className="upr-marquee py-3 md:py-4">
              {[...Array(2)].map((_, k) => (
                <div key={k} className="flex shrink-0 items-center">
                  {marqueeWords.map((w, i) => (
                    <span key={`${k}-${i}`} className="font-heading font-black text-2xl sm:text-3xl md:text-5xl tracking-tighter px-4 sm:px-6 md:px-8 inline-flex items-center gap-4 sm:gap-6 md:gap-8" style={{ letterSpacing: "-0.04em" }}>
                      {w}
                      <span className="inline-block w-2 h-2 md:w-3 md:h-3 rounded-full shrink-0" style={{ background: "var(--upr-primary)" }} />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* FEATURED */}
      {featured && !activeTag && (
        <section className="px-6 md:px-12 lg:px-16 pb-20 md:pb-28" data-testid="featured-section">
          <div className="upr-eyebrow mb-6">Featured Release</div>
          <Link to={`/release/${featured.id}`} className="block group" data-testid="featured-release">
            <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end">
              <div className={`${featuredSize.wrap} upr-cover`} style={{ aspectRatio: "1/1", border: "1px solid var(--upr-border)" }}>
                <img src={featured.cover_url} alt={featured.title} className="w-full h-full object-cover" />
              </div>
              <div className={featuredSize.side}>
                <div className="upr-eyebrow mb-3">{featured.catalog_number || "Featured"} · {featured.format}</div>
                <h2 className="font-heading font-black tracking-tighter leading-[0.9] text-5xl md:text-6xl" style={{ letterSpacing: "-0.03em" }}>
                  {featured.title}
                </h2>
                <div className="mt-3 font-heading font-bold text-2xl">{featured.artist_name}</div>
                {featured.description && (
                  <div
                    className="mt-5 text-base upr-featured-teaser"
                    style={{ color: "var(--upr-muted)" }}
                    data-testid="featured-description"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(featured.description, RICH_TEXT_SANITIZE) }}
                  />
                )}
                <div className="mt-6 inline-flex items-center gap-2 upr-link-underline font-heading uppercase tracking-widest text-sm">
                  Explore release <ArrowUpRight size={16} />
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* GRID */}
      <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32" data-testid="releases-grid-section">
        <div className="flex items-baseline justify-between mb-8 md:mb-10">
          <h3 className="font-heading font-black text-3xl md:text-4xl tracking-tighter" style={{ letterSpacing: "-0.03em" }}>
            {activeTag ? "Matching releases" : "The Catalogue"}
          </h3>
          <div className="upr-eyebrow">{(activeTag ? filtered : releases).length} releases</div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square animate-pulse" style={{ background: "var(--upr-surface)" }} />
            ))}
          </div>
        ) : rest.length === 0 ? (
          <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No further releases yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="releases-grid">
            {rest.map((r) => (
              <Link to={`/release/${r.id}`} key={r.id} className="block upr-card-hover" data-testid={`release-card-${r.id}`}>
                <div className="upr-cover aspect-square" style={{ border: "1px solid var(--upr-border)" }}>
                  <img src={r.cover_url} alt={r.title} className="w-full h-full object-cover" />
                </div>
                <div className="pt-3">
                  <div className="upr-eyebrow" style={{ fontSize: "0.62rem" }}>{r.catalog_number || r.year} · {r.format}</div>
                  <div className="font-heading font-extrabold text-lg leading-tight mt-1" style={{ letterSpacing: "-0.01em" }}>{r.title}</div>
                  {r.version && <div className="text-xs italic" style={{ color: "var(--upr-muted)" }}>{r.version}</div>}
                  <div className="text-sm" style={{ color: "var(--upr-muted)" }}>{r.artist_name}</div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {!activeTag && !loading && releases.length < total && (
          <div className="mt-10 flex justify-center" data-testid="releases-load-more-wrap">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="releases-load-more"
              className="upr-btn"
            >
              {loadingMore ? "Loading…" : `Load more (${total - releases.length} left)`}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
